import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DeviceOS } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  BiometricsConfig,
  EmployeeGroup,
  ExternalIdSource,
  MemberExternalId,
  MemberPhone,
  Patient,
  ProgramExtension,
} from '@vestahealthcare/common/models';
import { getDiff } from '@vestahealthcare/common/utils/api';

import { Panel } from 'styleguide-v2';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { fetchPatientById } from 'dash/src/redux/slices/MemberInfoSlice';
import { useSelector } from 'dash/src/redux/store';
import { CacheServices } from 'dash/src/services';
import { fetchEnabledConfigs } from 'dash/src/services/BiometricsServices';
import { getExternalIdSources } from 'dash/src/services/ExternalIdSourceServices';
import {
  Device,
  MemberContactInfoParsedResponse,
  createOrUpdateMemberContactInfo,
  getMemberContactInfoParsed,
  getMemberExternalIds,
  getMemberPhones,
  getPatientDevice,
  sendAppInvite,
  updateMemberExternalIds,
  updateMemberPhonesv2,
  updatePatient,
  updateProgramExtensions,
  updateSelfDirectingContactInfo,
} from 'dash/src/services/PatientServices';
import { useQueryParams } from 'dash/src/utils/useQueryParams';

import { CareTeamInfo } from './EditModals/CareTeamInfo';
import MemberConsents from './MemberConsents';
import MemberGuideDetails from './MemberGuideDetails';
import MemberInsurances from './MemberInsurances';
import { MemberPersonalDetails } from './MemberPersonalDetails';
import { MemberProgramDetails } from './MemberProgramDetails';
import MemberReferrals from './MemberReferrals';
import './main.less';
import {
  getContactInfoDiff,
  getMemberDetailDiff,
  getMemberPhonesDiff,
  getMemberProgramDiff,
  getProgramExtensionDiff,
} from './paramUtils';

const DashboardMemberDetails = () => {
  const params = useQueryParams();
  const dispatch = useDispatch();
  const patient = useSelector((state) => state.memberInfoSlice.patient);
  const memberLoading = useSelector((state) => state.memberInfoSlice.loading);
  const selfDirectingContactInfo = useSelector(
    (state) => state.memberInfoSlice.selfDirectingContactInfo,
  );

  const [biometricConfigs, setBiometricConfigs] = useState<BiometricsConfig[]>(
    [],
  );
  const [contactInfo, setContactInfo] = useState<
    MemberContactInfoParsedResponse
  >();
  const [externalIds, setExternalIds] = useState<MemberExternalId[]>([]);
  const [ipadDevice, setIpadDevice] = useState<Device>();
  const [memberPhones, setMemberPhones] = useState<MemberPhone[]>([]);

  const [externalIdSources, setExternalIdSources] = useState<
    ExternalIdSource[]
  >([]);
  const [programExtensions, setProgramExtensions] = useState<
    ProgramExtension[]
  >([]);
  const [selfReporterStatuses, setSelfReporterStatuses] = useState<BaseEnum[]>(
    [],
  );
  const [worklistGroups, setWorklistGroups] = useState<EmployeeGroup[]>([]);

  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [memberDataLoading, setMemberDataLoading] = useState<boolean>(true);
  const [consentsLoading, setConsentsLoading] = useState<boolean>(true);
  const [insurancesLoading, setInsurancesLoading] = useState<boolean>(true);
  const [referralsLoading, setReferralsLoading] = useState<boolean>(true);

  const refetchPatient = async (patient: Patient) => {
    dispatch(fetchPatientById(patient.id));
  };

  const fetchInitialData = async () => {
    setDataLoading(true);
    const [
      externalIdSources,
      programExtensions,
      selfReporterStatuses,
      worklistGroups,
    ] = await Promise.all([
      getExternalIdSources(),
      CacheServices.getAllProgramExtensions(),
      CacheServices.getCareTeamPersonStatuses(),
      CacheServices.getEmployeeGroupsWorklist(),
    ]);
    setExternalIdSources(externalIdSources);
    setProgramExtensions(programExtensions);
    setSelfReporterStatuses(selfReporterStatuses);
    setWorklistGroups(worklistGroups);
    setDataLoading(false);
  };

  const fetchMemberData = async () => {
    if (!patient) return;

    setMemberDataLoading(true);
    const [
      biometricConfigs,
      contactInfo,
      ipadId,
      externalIds,
      memberPhones,
    ] = await Promise.all([
      fetchEnabledConfigs(patient.id),
      getMemberContactInfoParsed(patient.id),
      getPatientDevice(patient.id),
      getMemberExternalIds(patient.id),
      getMemberPhones(patient.id),
    ]);
    setBiometricConfigs(biometricConfigs);
    setContactInfo(contactInfo);
    setExternalIds(externalIds);
    setIpadDevice(ipadId);
    setMemberPhones(memberPhones);
    setMemberDataLoading(false);
  };

  useEffect(() => {
    fetchMemberData();
  }, [patient?.id]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const anchor = params.get('anchor');
    if (
      anchor === 'consents' &&
      !consentsLoading &&
      !insurancesLoading &&
      !referralsLoading
    ) {
      document.querySelector('#page')?.scrollTo({
        top: 5000,
        behavior: 'smooth',
      });
    }
  }, [consentsLoading, insurancesLoading, referralsLoading]);

  if (!patient) {
    return null;
  }

  return (
    <Panel id="personal-details-section" data-cy="personal-details-section">
      <Panel.Heading title={translate('personalDetails.title')} />
      <Panel.Body
        loading={
          memberDataLoading ||
          consentsLoading ||
          insurancesLoading ||
          referralsLoading ||
          memberLoading ||
          dataLoading
        }
      >
        <MemberProgramDetails
          configs={biometricConfigs}
          contactInfo={contactInfo}
          data={{
            programExtensions,
            selfReporterStatuses,
            worklistGroups,
          }}
          member={patient}
          ipad={ipadDevice}
          onSendAppInvite={async (member, deviceOs) =>
            await sendAppInvite(
              member.id,
              deviceOs === DeviceOS.IOS ? 'ios' : 'android',
            )
          }
          onSubmit={async (params) => {
            if (!patient) return false;

            try {
              const {
                contactInfo: contactInfoParams,
                member: memberParams,
                programExtensions: programExtensionParams,
              } = params;

              const promises = [] as Promise<any>[];

              const memberDiffParams =
                memberParams && getMemberProgramDiff(patient, memberParams);

              if (memberDiffParams && Object.keys(memberDiffParams).length) {
                promises.push(updatePatient(patient.id, memberDiffParams));
              }

              if (
                contactInfoParams &&
                getContactInfoDiff(contactInfo, contactInfoParams)
              ) {
                promises.push(
                  createOrUpdateMemberContactInfo(
                    patient.id,
                    contactInfoParams,
                  ),
                );
              }

              if (
                programExtensionParams &&
                getProgramExtensionDiff(
                  patient.programExtensions,
                  programExtensionParams,
                )
              ) {
                promises.push(
                  updateProgramExtensions(patient.id, programExtensionParams),
                );
              }

              await Promise.all(promises);

              if (promises.length) {
                setTimeout(() => {
                  refetchPatient(patient);
                  fetchMemberData();
                }, 250);
              }
            } catch (e) {
              showGlobalError(e as string);
              return false;
            }
            return true;
          }}
        />
        <br />
        <MemberPersonalDetails
          data={{
            externalIdSources,
          }}
          member={patient}
          memberExternalIds={externalIds}
          onSubmit={async (params) => {
            if (!patient) return false;

            try {
              const {
                externalIds: paramExternalIds,
                phones,
                deletedPhones,
                inactivatedPhones,
                reactivatedPhones,
                proxyContact,
                member: memberParams,
              } = params;

              const promises: Promise<any>[] = [];

              const memberDiffParams =
                memberParams && getMemberDetailDiff(patient, memberParams);

              if (
                memberParams &&
                memberDiffParams &&
                Object.keys(memberDiffParams).length
              ) {
                promises.push(updatePatient(patient.id, memberDiffParams));
              }

              if (
                deletedPhones?.length ||
                reactivatedPhones?.length ||
                getMemberPhonesDiff(memberPhones, phones, inactivatedPhones)
              ) {
                promises.push(
                  updateMemberPhonesv2(patient.id, {
                    phones,
                    deletedPhones,
                    inactivatedPhones,
                    reactivatedPhones,
                  }),
                );
              }

              if (
                getDiff({ e: externalIds }, { e: paramExternalIds || [] }).e
              ) {
                promises.push(
                  updateMemberExternalIds(
                    patient.id,
                    (paramExternalIds || []).map(
                      ({ externalId, externalIdSource: source }) => ({
                        externalId,
                        externalIdSourceId: source.id,
                      }),
                    ),
                  ),
                );
              }

              if (
                memberParams &&
                !memberParams.selfDirecting &&
                proxyContact &&
                getDiff({ p: selfDirectingContactInfo }, { p: proxyContact }).p
              ) {
                promises.push(
                  updateSelfDirectingContactInfo(patient.id, proxyContact),
                );
              }

              await Promise.all(promises);

              if (promises.length) {
                setTimeout(() => {
                  refetchPatient(patient);
                  fetchMemberData();
                }, 250);
              }
            } catch (e) {
              showGlobalError(e as string);
              return false;
            }
            return true;
          }}
          phoneNumbers={memberPhones}
          selfDirectingContactInfo={selfDirectingContactInfo}
        />
        <br />
        <MemberReferrals member={patient} onLoading={setReferralsLoading} />
        <br />
        <br />
        <MemberInsurances member={patient} onLoading={setInsurancesLoading} />
        {patient.hasGuideProgramExtension() && (
          <>
            <br />
            <br />
            <MemberGuideDetails member={patient} />
          </>
        )}
        <br />
        <br />
        <MemberConsents member={patient} onLoading={setConsentsLoading} />
        <br />
        <br />
        <CareTeamInfo patient={patient} onSubmit={refetchPatient} />
        <br />
      </Panel.Body>
    </Panel>
  );
};

export default DashboardMemberDetails;
